import { useEffect, useState } from 'react';
import { Check, X } from 'lucide-react';
import { axiosInstance } from '@edison/api-client-react';

interface ExtractedAttendee {
  name: string;
  userId?: string;
}

interface ProjectAllocation {
  projectId: string;
  projectName: string;
  allocationPct: number;      // current value (human edits this)
  aiAllocationPct: number;    // original AI value (never changes)
  activityIds: string[];
}

interface ProjectActivity {
  id: string;
  name: string;
}

export interface PendingArtefact {
  id: string;
  title: string;
  uploadFilename?: string | null;
  metadata?: {
    extractedAttendees?: ExtractedAttendee[];
    extractedDurationMin?: number | null;
    extractedDate?: string | null;
    extractedProjectAllocations?: Array<{
      projectId: string;
      projectName: string;
      allocationPct: number;
      suggestedActivityIds: string[];
    }>;
  } | null;
}

interface ConfirmCardProps {
  artefact: PendingArtefact;
  onConfirmed: () => void;
  onDiscard: () => void;
}

export function ConfirmCard({ artefact, onConfirmed, onDiscard }: ConfirmCardProps) {
  const meta = artefact.metadata ?? {};
  const extracted = meta.extractedProjectAllocations ?? [];

  const [title, setTitle] = useState(artefact.title);
  const [date, setDate] = useState(meta.extractedDate ?? '');
  const [attendees, setAttendees] = useState<ExtractedAttendee[]>(meta.extractedAttendees ?? []);
  const [allocations, setAllocations] = useState<ProjectAllocation[]>(
    extracted.map((e) => ({
      projectId: e.projectId,
      projectName: e.projectName,
      allocationPct: e.allocationPct,
      aiAllocationPct: e.allocationPct,
      activityIds: e.suggestedActivityIds,
    })),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activitiesByProject, setActivitiesByProject] = useState<Map<string, ProjectActivity[]>>(new Map());

  // Fetch each distinct project's activity list once on mount so the AI-suggested
  // activityIds can be rendered as real names (never invent names — Rule Zero).
  useEffect(() => {
    let cancelled = false;
    const projectIds = Array.from(new Set(extracted.map((e) => e.projectId)));

    void Promise.all(
      projectIds.map(async (projectId) => {
        try {
          const res = await axiosInstance.get<{ data: { items: Array<{ id: string; name: string; activityType?: string }> } }>(
            `/api/projects/${projectId}/activities`,
          );
          if (cancelled) return;
          const items = res.data?.data?.items ?? [];
          setActivitiesByProject((prev) => {
            const next = new Map(prev);
            next.set(projectId, items.map((a) => ({ id: a.id, name: a.name })));
            return next;
          });
        } catch {
          if (cancelled) return;
          setActivitiesByProject((prev) => {
            const next = new Map(prev);
            next.set(projectId, []);
            return next;
          });
        }
      }),
    );

    return () => {
      cancelled = true;
    };
    // Deliberately runs once on mount only — extracted's project ids are fixed for
    // the lifetime of this card.
  }, []);

  function removeAllocation(projectId: string) {
    setAllocations((prev) => prev.filter((a) => a.projectId !== projectId));
  }

  function updateAllocationPct(projectId: string, pct: number) {
    setAllocations((prev) =>
      prev.map((a) => (a.projectId === projectId ? { ...a, allocationPct: Math.max(0, Math.min(100, pct)) } : a)),
    );
  }

  function toggleActivity(projectId: string, activityId: string) {
    setAllocations((prev) =>
      prev.map((a) =>
        a.projectId === projectId
          ? {
              ...a,
              activityIds: a.activityIds.includes(activityId)
                ? a.activityIds.filter((id) => id !== activityId)
                : [...a.activityIds, activityId],
            }
          : a,
      ),
    );
  }

  function removeAttendee(name: string) {
    setAttendees((prev) => prev.filter((a) => a.name !== name));
  }

  async function handleConfirm() {
    setSaving(true);
    setError(null);
    try {
      await axiosInstance.post(`/api/artefacts/${artefact.id}/confirm`, {
        title,
        date: date || null,
        attendees,
        projectAllocations: allocations.map((a) => ({
          projectId: a.projectId,
          activityIds: a.activityIds,
          aiAllocationPct: a.aiAllocationPct,
          humanAllocationPct: a.allocationPct,
        })),
      });
      onConfirmed();
    } catch {
      setError('Failed to save — please try again');
    } finally {
      setSaving(false);
    }
  }

  const durationMin = meta.extractedDurationMin;

  return (
    <div className="rounded-xl border border-[oklch(0.55_0.18_270/0.4)] bg-gradient-to-br from-[oklch(0.13_0.03_270)] to-[oklch(0.15_0.01_250)] overflow-hidden shadow-lg">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-[oklch(0.55_0.18_270/0.15)] bg-[oklch(0.55_0.18_270/0.08)] px-4 py-3">
        <span className="rounded bg-[oklch(0.55_0.18_270)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
          AI extracted
        </span>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          aria-label="Meeting title"
          className="flex-1 bg-transparent text-[13px] font-semibold text-[oklch(0.88_0.06_270)] outline-none placeholder:text-[oklch(0.65_0.06_270)]"
          placeholder="Meeting title"
        />
        <button
          onClick={onDiscard}
          aria-label="Discard this extraction"
          className="text-gray-400 hover:text-gray-100 transition-colors"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <div className="p-4 space-y-4">
        {/* Meta row */}
        <div className="flex flex-wrap gap-6 text-[11.5px]">
          <div>
            <div className="uppercase tracking-wider text-gray-400 mb-1 text-[10px]">Type</div>
            <div className="text-gray-100">Meeting transcript</div>
          </div>
          <div>
            <div className="uppercase tracking-wider text-gray-400 mb-1 text-[10px]">Date</div>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              aria-label="Meeting date"
              className="bg-transparent text-gray-100 outline-none border-b border-gray-700 focus:border-[oklch(0.55_0.18_270)] transition-colors"
            />
          </div>
          {durationMin != null && (
            <div>
              <div className="uppercase tracking-wider text-gray-400 mb-1 text-[10px]">Duration</div>
              <div className="text-gray-100">{durationMin} min</div>
            </div>
          )}
          {artefact.uploadFilename && (
            <div>
              <div className="uppercase tracking-wider text-gray-400 mb-1 text-[10px]">File</div>
              <div className="text-gray-400 truncate max-w-[200px]">{artefact.uploadFilename}</div>
            </div>
          )}
        </div>

        {/* Attendees */}
        {attendees.length > 0 && (
          <div>
            <div className="mb-2 text-[10px] uppercase tracking-wider text-gray-400">Attendees</div>
            <div className="flex flex-wrap gap-2">
              {attendees.map((a) => (
                <span
                  key={a.name}
                  className="flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1 text-[11.5px] text-gray-400"
                >
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[oklch(0.55_0.18_270)] text-[9px] font-bold text-white" aria-hidden="true">
                    {a.name.charAt(0).toUpperCase()}
                  </span>
                  {a.name}
                  <button
                    onClick={() => removeAttendee(a.name)}
                    aria-label={`Remove ${a.name}`}
                    className="text-gray-500 hover:text-gray-300 transition-colors"
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Project allocations */}
        <div>
          <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-400">
            Projects discussed
            <span className="normal-case font-normal opacity-60">(AI breakdown — edit freely)</span>
          </div>
          <div className="space-y-2">
            {allocations.length === 0 && (
              <p className="text-[11.5px] text-gray-400 italic">No projects detected — confirm anyway or discard.</p>
            )}
            {allocations.map((alloc) => {
              const isEdited = alloc.allocationPct !== alloc.aiAllocationPct;
              const projectActivities = activitiesByProject.get(alloc.projectId);
              return (
                <div
                  key={alloc.projectId}
                  className="rounded-lg border border-gray-700 bg-[oklch(0.13_0.006_250)] p-3"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="flex-1 text-[13px] font-semibold text-gray-100">{alloc.projectName}</span>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={alloc.allocationPct}
                        onChange={(e) => updateAllocationPct(alloc.projectId, Number(e.target.value))}
                        aria-label={`Allocation percentage for ${alloc.projectName}`}
                        className={[
                          'w-14 rounded border px-2 py-0.5 text-[11.5px] text-right outline-none',
                          isEdited
                            ? 'border-[oklch(0.55_0.18_270)] bg-[oklch(0.55_0.18_270/0.1)] text-[oklch(0.80_0.10_270)]'
                            : 'border-gray-700 bg-white/5 text-gray-400',
                        ].join(' ')}
                      />
                      <span className="text-[11.5px] text-gray-400">%</span>
                    </div>
                    <button
                      onClick={() => removeAllocation(alloc.projectId)}
                      aria-label={`Remove ${alloc.projectName}`}
                      className="text-gray-500 hover:text-rose-400 transition-colors"
                    >
                      <X className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </div>
                  {/* Progress bar */}
                  <div className="h-1 rounded-full bg-white/5 mb-2">
                    <div
                      role="progressbar"
                      aria-valuenow={alloc.allocationPct}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      className={['h-1 rounded-full transition-all', isEdited ? 'bg-[oklch(0.55_0.18_270)]' : 'bg-[oklch(0.55_0.18_270/0.4)]'].join(' ')}
                      style={{ width: `${Math.min(alloc.allocationPct, 100)}%` }}
                    />
                  </div>
                  {/* Activities — toggleable, real names only (Rule Zero: never invent names) */}
                  <div className="mb-1 text-[10px] uppercase tracking-wider text-gray-500">Activities</div>
                  {projectActivities === undefined && (
                    <p className="text-[10.5px] text-gray-500 italic">Loading activities…</p>
                  )}
                  {projectActivities !== undefined && projectActivities.length === 0 && (
                    <p className="text-[10.5px] text-gray-500 italic">No activities in this project yet</p>
                  )}
                  {projectActivities !== undefined && projectActivities.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {projectActivities.map((act) => {
                        const selected = alloc.activityIds.includes(act.id);
                        return (
                          <button
                            key={act.id}
                            type="button"
                            onClick={() => toggleActivity(alloc.projectId, act.id)}
                            aria-pressed={selected}
                            className={[
                              'flex items-center gap-1 rounded border px-2 py-0.5 text-[10.5px] transition-colors',
                              selected
                                ? 'border-[oklch(0.72_0.12_155/0.4)] bg-[oklch(0.42_0.12_155/0.15)] text-[oklch(0.72_0.12_155)]'
                                : 'border-gray-700 bg-white/5 text-gray-400 hover:text-gray-300',
                            ].join(' ')}
                          >
                            {selected && <Check className="h-2.5 w-2.5" aria-hidden="true" />}
                            {act.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        {error && <p className="text-[11.5px] text-rose-400">{error}</p>}
        <div className="flex items-center gap-2 border-t border-gray-700 pt-3">
          <button
            onClick={() => void handleConfirm()}
            disabled={saving}
            className="rounded-lg bg-[oklch(0.55_0.18_270)] px-4 py-1.5 text-[13px] font-semibold text-white hover:bg-[oklch(0.60_0.18_270)] disabled:opacity-60 transition-colors"
          >
            {saving ? 'Saving…' : 'Confirm & Save'}
          </button>
          <button
            onClick={onDiscard}
            className="rounded-lg border border-gray-700 px-3 py-1.5 text-[13px] text-gray-400 hover:text-gray-100 hover:border-gray-500 transition-colors"
          >
            Discard
          </button>
          <div className="ml-auto flex items-center gap-1.5 text-[11px] text-gray-400">
            <span className="h-1.5 w-1.5 rounded-full bg-[oklch(0.55_0.14_155)]" aria-hidden="true" />
            {(meta.extractedProjectAllocations?.length ?? 0) > 0 ? 'Projects detected' : 'No projects detected'}
          </div>
        </div>
      </div>
    </div>
  );
}
